import React, { useCallback, useEffect, useState } from 'react';
import {
  Button,
  FormControl,
  Grid, InputLabel, MenuItem, Select, SelectChangeEvent,
  TextField,
  Typography,
  FormLabel,
  Checkbox,
} from '@mui/material';

import {
  REIMBURSEMENT_CATEGORIES,
  ReimbursementOrderService,
  User,
  UserVoucher,
  UserVoucherService,
} from 'us-web-services';
import toast from 'react-hot-toast';
import moment from 'moment';
import { DatePicker, TimePicker } from '@mui/x-date-pickers';
import DisplayService from '../../../util/DisplayService';
import PageStyles from '../../../styles/PageStyles';
import DateUtil from '../../../util/DateUtil';
import ReimbursementLookup from './ReimbursementLookup';

interface Props {
  user: User;
  history: {
    push(location: string): void;
  };
}

export default function AddReimbursement(props: Props) {
  const { history, user } = props;
  const { classes } = PageStyles();
  const [amount, setAmount] = useState(0);
  const [bookingId, setBookingId] = useState<number>();
  const [saving, setSaving] = useState(false);
  const [userVouchers, setUserVouchers] = useState<UserVoucher[]>([]);
  const [selectedVouchers, setSelectedVouchers] = useState<number[]>([]);
  const [selectedCareTypeCategory, setSelectedCareTypeCategory] = useState<string>('');
  const [qualifiedDependent, setQualifiedDependent] = useState<boolean>(false);
  const [startDate, setStartDate] = useState<Date>();
  const [startTime, setStartTime] = useState<moment.Moment>();
  const [endDate, setEndDate] = useState<Date>();
  const [endTime, setEndTime] = useState<moment.Moment>();

  const updateAmount = event => {
    setAmount(event.target.value);
  };

  const updateBookingId = event => {
    const value = event.target.value;

    setBookingId(value ? parseInt(value, 10) : undefined);
  };

  const onVoucherSelect = (event: SelectChangeEvent<number[]>) => {
    setSelectedVouchers(event.target.value as number[]);
  };

  const onCareTypeCategorySelect = (event: SelectChangeEvent<string>) => {
    setSelectedCareTypeCategory(event.target.value);
  };

  const getCurrentYearEnd = () => {
    const now = new Date();

    return new Date(now.getFullYear(), 11, 31, 23, 59, 59);
  };

  const updateStartDate = (newDate: Date | null) => {
    setStartDate(newDate);
  };

  const updateStartTime = (newTime: moment.Moment | null) => {
    setStartTime(newTime);
  };

  const updateEndDate = (newDate: Date | null) => {
    if (!newDate) {
      setEndDate(null);

      return;
    }

    const yearEnd = getCurrentYearEnd();

    if (newDate > yearEnd) {
      toast.error(`End date cannot be after ${yearEnd}`);

      return;
    }

    if (startDate && newDate < startDate) {
      toast.error('End date cannot be before start date');

      return;
    }

    setEndDate(newDate);
  };

  const updateEndTime = (newTime: moment.Moment | null) => {
    setEndTime(newTime);
  };

  const showError = (error, defaultMessage) => {
    const displayedError = DisplayService.getErrorResponse(
      error,
      defaultMessage,
    );

    toast.error(displayedError.message);
  };

  const getDateTime = (date: Date, time?: moment.Moment) => {
    if (!date) return null;

    const result = moment(date);

    if (time) {
      result.hours(time.hours());
      result.minutes(time.minutes());
    } else {
      result.hours(0);
      result.minutes(0);
    }

    result.seconds(0);

    return result.format(DateUtil.dateTimeFormat);
  };

  const reimburse = async () => {
    if (!amount) {
      toast.error('Amount must be greater than 0');

      return;
    }

    if (!selectedVouchers || selectedVouchers.length === 0) {
      toast.error('At least one user voucher must be selected');

      return;
    }

    if (!selectedCareTypeCategory) {
      toast.error('Care type category is required');

      return;
    }

    if (!startDate || !endDate) {
      toast.error('Both start and end dates are required');

      return;
    }

    if (endDate < startDate) {
      toast.error('End date cannot be before start date');

      return;
    }

    setSaving(true);

    const startDateFormat = getDateTime(startDate, startTime);
    const endDateFormat = getDateTime(endDate, endTime);

    const itemVouchers = selectedVouchers.map(id => ({ userVoucherId: id }));

    const data = {
      userId: user.id,
      total: amount,
      itemVouchers,
      itemBooking: {
        bookingId,
      },
      usOon: {
        startDate: startDateFormat,
        endDate: endDateFormat,
        careTypeCategory: selectedCareTypeCategory,
        qualifiedDependent,
      },
    };

    try {
      await ReimbursementOrderService.reimburse(data);
      toast.success('Reimbursement succeeded');
      history.push(`/users/lookup?id=${user.id}`);
    } catch (error) {
      showError(error, 'Reimbursement failed');
    } finally {
      setSaving(false);
    }
  };

  const getVouchers = useCallback(async () => {
    const voucherFilter = {
      params: {
        userId: user.id,
        limit: 100,
        expired: true,
      },
    };

    try {
      const vouchersResponse = (await UserVoucherService.getByFilter(voucherFilter)).data;

      setUserVouchers(vouchersResponse.data);
    } catch (error) {
      showError(error, 'Vouchers could not be loaded.');
      setUserVouchers([]);
    }
  }, [user.id]);

  const updateQualifiedDependent = () => {
    setQualifiedDependent(!qualifiedDependent);
  };

  useEffect(() => {
    getVouchers();
  }, [getVouchers]);

  return (
    <div style={{ padding: 10 }}>
      <Typography variant='h6' gutterBottom>
        Reimburse {user.firstName} {user.lastName} (UID: {user.id})
      </Typography>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <FormControl sx={{ minWidth: 300 }}>
            <InputLabel id='voucher-select-placeholder' required>Select Vouchers</InputLabel>
            <Select
              id='voucher-select'
              label='Select Vouchers'
              value={selectedVouchers}
              onChange={onVoucherSelect}
              multiple
            >
              {userVouchers
                .filter(item => {
                  const isExpired = item.validUntil && new Date(item.validUntil) < new Date();
                  const isPercentDiscount = item.voucher.percentDiscount;

                  return (((!isExpired && !isPercentDiscount) || (isExpired && !isPercentDiscount))
                    && item.currentValue > 0);
                })
                .map(item => (
                  <MenuItem value={item.id} key={item.id}>
                    {`${item.voucher.code} - $${item.currentValue}
                      ${item.validUntil && new Date(item.validUntil) < new Date() ? ' (expired)' : ''}
                      ${item.active ? '' : ' (deactivated)'}`
                    }
                  </MenuItem>
                ))
              }
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12}>
          <FormControl sx={{ minWidth: 300 }}>
            <InputLabel id='care-type-category-label' required>Care type category</InputLabel>
            <Select
              labelId='care-type-category-label'
              id='care-type-category-select'
              value={selectedCareTypeCategory}
              onChange={onCareTypeCategorySelect}
              label='Care type category'
            >
              {REIMBURSEMENT_CATEGORIES
                .map(item => (
                  <MenuItem value={item} key={item}>
                    {item}
                  </MenuItem>
                ))
              }
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12}>
          <FormLabel component='legend'>Qualified dependent</FormLabel>
          <Checkbox
            checked={qualifiedDependent}
            onChange={updateQualifiedDependent}
            inputProps={{
              'aria-label': 'qualified dependent checkbox',
            }}
          />
        </Grid>

        <Grid item xs={5}>
          <DatePicker
            label='Start Date'
            value={startDate || null}
            onChange={updateStartDate}
            renderInput={(inputProps) => (
              <TextField
                {...inputProps}
                required
                fullWidth
                name='startDate'
                id='startDate'
              />
            )}
          />
        </Grid>
        <Grid item xs={5}>
          <TimePicker
            label='Start Time (Optional)'
            value={startTime || null}
            onChange={updateStartTime}
            renderInput={(inputProps) => (
              <TextField
                {...inputProps}
                fullWidth
                name='startTime'
                id='startTime'
              />
            )}
          />
        </Grid>
        <Grid item xs={5}>
          <DatePicker
            label='End Date'
            value={endDate || null}
            onChange={updateEndDate}
            maxDate={getCurrentYearEnd()}
            renderInput={(inputProps) => (
              <TextField
                {...inputProps}
                required
                fullWidth
                name='endDate'
                id='endDate'
              />
            )}
          />
        </Grid>
        <Grid item xs={5}>
          <TimePicker
            label='End Time (Optional)'
            value={endTime || null}
            onChange={updateEndTime}
            renderInput={(inputProps) => (
              <TextField
                {...inputProps}
                fullWidth
                name='endTime'
                id='endTime'
              />
            )}
          />
        </Grid>
        <Grid item xs={12}>
          <Grid item xs={12} sm={5}>
            <TextField
              required
              id='amount'
              label='Amount to Deduct'
              value={amount}
              onChange={updateAmount}
            />
          </Grid>
        </Grid>
        <Grid item xs={12}>
          <Grid item xs={12} sm={5}>
            <TextField
              id='bookingId'
              label='Booking ID'
              value={bookingId ?? ''}
              onChange={updateBookingId}
            />
          </Grid>
        </Grid>
      </Grid>
      <div className={classes.buttons} style={{ justifyContent: 'left' }}>
        <Button
          disabled={saving}
          variant='contained'
          color='primary'
          onClick={reimburse}
          className={classes.button}
        >
          {saving ? 'Reimbursing...' : 'Reimburse'}
        </Button>
        <br />
      </div>
      <Grid item xs={12} sx={{ mt: 4 }}>
        <ReimbursementLookup />
      </Grid>
    </div>
  );
}
